import * as signalR from "@microsoft/signalr";
import { appData } from "@/helpers/global";

export function createMeetingRealtimeService(getAccessToken) {
    const state = {
        hub: null,
        channelName: null,
        rtcUid: null,          // reconnect sonrası re-join için sakla
        displayName: null,     // reconnect sonrası re-join için sakla

        uidNameMap: new Map(),
        connected: false,
        onNamesChanged: null,
        onParticipantsChanged: null,

        // Whiteboard handler listesi
        wbHandlers: new Set(),

        // Connected değilken sendWhiteboard invoke patlamasın diye queue
        wbQueue: [],
        wbFlushLock: false,
        hubStartPromise: null
    };

    function bumpParticipants() {
        if (typeof state.onParticipantsChanged === "function") {
            state.onParticipantsChanged();
        }
    }

    function emitNamesChangedOnce() {
        if (typeof state.onNamesChanged === "function") {
            state.onNamesChanged();
        }
    }

    function setName(uid, name, opts = {}) {
        if (uid === null || uid === undefined) {
            return;
        }

        const safe = (name || "").trim();
        if (!safe) {
            return;
        }

        state.uidNameMap.set(String(uid), safe);

        if (!opts.silent) {
            emitNamesChangedOnce();
        }
    }

    function resolveName(uid) {
        const key = String(uid);
        if (state.uidNameMap.has(key)) {
            return state.uidNameMap.get(key);
        }
        return `ID: ${key}`;
    }

    function isConnected() {
        if (!state.hub) {
            return false;
        }
        return state.hub.state === signalR.HubConnectionState.Connected;
    }

    async function startHubIfNeeded() {
        if (!state.hub) {
            return;
        }

        if (isConnected()) {
            state.connected = true;
            return;
        }

        // start zaten çalışıyorsa aynı promise beklenir
        if (state.hubStartPromise) {
            await state.hubStartPromise;
            return;
        }

        // Reconnecting iken start denemiyoruz (otomatik reconnect devam etsin)
        if (state.hub.state === signalR.HubConnectionState.Reconnecting) {
            state.connected = false;
            return;
        }

        state.hubStartPromise = state.hub.start()
            .then(() => {
                state.connected = true;
            })
            .catch((err) => {
                state.connected = false;
                throw err;
            })
            .finally(() => {
                state.hubStartPromise = null;
            });

        await state.hubStartPromise;
    }

    async function flushWhiteboardQueue() {
        if (!state.hub) {
            return;
        }
        if (!isConnected()) {
            return;
        }
        if (state.wbFlushLock) {
            return;
        }

        state.wbFlushLock = true;
        try {
            while (state.wbQueue.length > 0 && isConnected()) {
                const item = state.wbQueue.shift();
                try {
                    await state.hub.invoke("SendWhiteboard", item.channelName, item.payload);
                } catch {
                    // Gönderim anında tekrar koparsa tekrar queue'ya alıp çıkıyoruz
                    state.wbQueue.unshift(item);
                    break;
                }
            }
        } finally {
            state.wbFlushLock = false;
        }
    }

    async function rejoinIfNeeded() {
        // reconnect sonrası server group/presence resetleniyorsa tekrar JoinMeeting
        if (!state.hub) {
            return;
        }
        if (!isConnected()) {
            return;
        }
        if (!state.channelName) {
            return;
        }
        if (state.rtcUid === null || state.rtcUid === undefined) {
            return;
        }

        try {
            await state.hub.invoke("JoinMeeting", state.channelName, String(state.rtcUid), state.displayName || "");
        } catch { /* ignore */ }
    }

    async function connect() {
        // hub hiç yoksa kur
        if (!state.hub) {
            state.hub = new signalR.HubConnectionBuilder()
                .withUrl(appData.serverBase + "hubs/meeting", {
                    accessTokenFactory: () => {
                        const t = getAccessToken();
                        return t || "";
                    }
                })
                .withAutomaticReconnect()
                .configureLogging(signalR.LogLevel.Warning)
                .build();

            state.hub.onreconnecting(() => {
                state.connected = false;
            });

            state.hub.onreconnected(async (_connectionId) => {
                state.connected = true;

                // TODO: Önemli Fark => reconnect sonrası yeniden JoinMeeting + tick tetiklenmesi için participants eventleri serverdan tekrar geleceği varsayımı
                await rejoinIfNeeded();

                await flushWhiteboardQueue();
            });

            state.hub.onclose(() => {
                state.connected = false;
            });

            state.hub.on("presence-sync", (payload) => {
                state.uidNameMap.clear();

                const list = (payload && payload.participants) ? payload.participants : [];
                (list || []).forEach((p) => {
                    setName(p.uid, p.name, { silent: true }); // tek sefer update
                });

                emitNamesChangedOnce();
                bumpParticipants(); // ✅ participantsTick için
            });

            state.hub.on("presence-joined", (payload) => {
                setName(payload.uid, payload.name);
                bumpParticipants(); // ✅ participantsTick için
            });

            state.hub.on("presence-left", (payload) => {
                // İstersen sil, istersen tut
                // state.uidNameMap.delete(String(payload.uid));
                bumpParticipants(); // ✅ participantsTick için (isim map değişmese bile)
            });

            // Whiteboard mesajlarını buradan dağıt
            state.hub.on("whiteboard-message", (payload) => {
                state.wbHandlers.forEach((fn) => {
                    try { fn(payload); } catch { /* ignore */ }
                });
            });
        }

        // "hub var" yetmez; Connected olmayı garanti et
        await startHubIfNeeded();

        // Connected olduysa birikeni gönder
        await flushWhiteboardQueue();
    }

    async function joinMeeting({ channelName, rtcUid, displayName }) {
        await connect();

        state.channelName = channelName;
        state.rtcUid = rtcUid;                 // reconnect için sakla
        state.displayName = displayName || ""; // reconnect için sakla

        setName(rtcUid, displayName);

        // invoke öncesi Connected garanti
        await startHubIfNeeded();
        await state.hub.invoke("JoinMeeting", channelName, String(rtcUid), displayName || "");
    }

    async function leaveMeeting() {
        if (!state.hub) {
            return;
        }

        if (state.channelName) {
            try {
                if (isConnected()) {
                    await state.hub.invoke("LeaveMeeting", state.channelName);
                }
            } catch { /* ignore */ }
        }

        state.channelName = null;
        state.rtcUid = null;
        state.displayName = null;

        // TODO: Önemli Fark => meeting kapanınca UI tarafında liste tazelensin isteniyorsa tick
        bumpParticipants();
    }

    async function setMeetingPresentation(channelName, enabled) {
        if (!channelName) {
            return;
        }

        await connect();
        await startHubIfNeeded();

        if (!isConnected()) {
            return;
        }

        await state.hub.invoke("SetMeetingPresentation", channelName, !!enabled);
    }

    async function disconnect() {
        if (!state.hub) {
            return;
        }

        try {
            await leaveMeeting();
        } finally {
            try { await state.hub.stop(); } catch { /* ignore */ }
            state.hub = null;
            state.connected = false;
            state.wbHandlers.clear();
            state.wbQueue.length = 0;      // referans bozma
            state.hubStartPromise = null;
        }
    }

    function setOnNamesChanged(fn) { state.onNamesChanged = fn; }
    function setOnParticipantsChanged(fn) { state.onParticipantsChanged = fn; }

    // ===================== WHITEBOARD TRANSPORT =====================
    async function sendWhiteboard(meetingId, payload) {
        // her send’de gereksiz start/kurulum olmasın; hub yoksa connect et
        if (!state.hub) {
            await connect();
        } else {
            try { await startHubIfNeeded(); } catch { /* ignore */ }
        }

        // rtc/signalr channel ayrışmasın; state.channelName yoksa gönderme
        const channelName = state.channelName;
        if (!channelName) {
            return;
        }

        if (!isConnected()) {
            state.wbQueue.push({ channelName, payload });
            try { await startHubIfNeeded(); } catch { /* ignore */ }
            await flushWhiteboardQueue();
            return;
        }

        try {
            await state.hub.invoke("SendWhiteboard", channelName, payload);
        } catch {
            // send anında koparsa queue'ya geri al
            state.wbQueue.push({ channelName, payload });
        }
    }

    function onWhiteboard(meetingId, handler) {
        if (!handler) {
            return;
        }
        state.wbHandlers.add(handler);
    }

    function offWhiteboard(meetingId, handler) {
        if (!handler) {
            return;
        }
        state.wbHandlers.delete(handler);
    }
    // ===================== /WHITEBOARD TRANSPORT =====================

    return {
        state,
        connect,
        joinMeeting,
        leaveMeeting,        
        setMeetingPresentation,
        disconnect,
        resolveName,
        setName,
        setOnNamesChanged,
        setOnParticipantsChanged,

        // expose
        sendWhiteboard,
        onWhiteboard,
        offWhiteboard,
    };
}
